import { useCallback, useContext, useEffect, useState } from 'react';
import { initialFingers } from './Model';
import { BiometricContext } from '../../utility/context/Biometric';
import { toast } from 'react-toastify';

const toastOptions = {
	position: 'bottom-right',
	autoClose: 2500,
	hideProgressBar: true,
	closeOnClick: true,
	theme: 'colored'
};

const formatQueuedDedo = (
	{ dedo_id, mano_id },
	biometric_type,
	customType = null
) => ({
	mano_id,
	dedo_id,
	biometric_type,
	id: `${mano_id}+${dedo_id}`,
	type:
		customType !== null
			? customType
			: biometric_type === 1
			? '1'
			: dedo_id === 'pulgar'
			? 'P'
			: mano_id === 'izquierda'
			? '4I'
			: '4D',
	user_readable: `el dedo ${(dedo_id !== 'menique'
		? dedo_id
		: 'meñique'
	).toUpperCase()} de la mano ${mano_id.toUpperCase()}`,
	requestId: null
});

const sortDedosByBiometricType = (dedos, biometric_type) => {
	let dedos_sorted = [];
	if (biometric_type === 1) {
		dedos_sorted = [...dedos.sort((a, b) => (a.mano_id < b.mano_id ? 1 : -1))];
	} else {
		const dedos_pulgares = dedos?.filter((dedo) => dedo.dedo_id === 'pulgar');
		const dedos_izquierda = dedos?.filter(
			(dedo) => dedo.mano_id === 'izquierda' && dedo.dedo_id !== 'pulgar'
		);
		const dedos_derecha = dedos?.filter(
			(dedo) => dedo.mano_id === 'derecha' && dedo.dedo_id !== 'pulgar'
		);
		dedos_sorted = [...dedos_izquierda, ...dedos_derecha, ...dedos_pulgares];
	}
	return dedos_sorted;
};

export function useHandsHandler(initialDedos) {
	// Estados de dedos para vista
	const [izquierda, setIzquierda] = useState(initialDedos?.izquierda ? initialDedos.izquierda : { ...initialFingers });
	const [derecha, setDerecha] = useState(initialDedos?.derecha ? initialDedos.derecha : { ...initialFingers });

	const onClickDedo = useCallback(
		(mano, dedo, type) => {
			if (type) {
				if (type === '4D') {
					// formik.setFieldValue('dedos', [
					setDerecha({
						...derecha,
						indice: +!derecha[dedo],
						medio: +!derecha[dedo],
						menique: +!derecha[dedo],
						anular: +!derecha[dedo]
					});
				} else if (type === '4I') {
					// formik.setFieldValue('dedos', [
					setIzquierda({
						...izquierda,
						indice: +!izquierda[dedo],
						medio: +!izquierda[dedo],
						menique: +!izquierda[dedo],
						anular: +!izquierda[dedo]
					});
				} else {
					setDerecha({
						...derecha,
						pulgar: +!(derecha['pulgar'] + izquierda['pulgar'])
					});
					setIzquierda({
						...izquierda,
						pulgar: +!(derecha['pulgar'] + izquierda['pulgar'])
					});
				}
			} else {
				mano === 'derecha'
					? setDerecha({ ...derecha, [dedo]: +!derecha[dedo] })
					: setIzquierda({ ...izquierda, [dedo]: +!izquierda[dedo] });
			}
		},
		[izquierda, derecha]
	);

	useEffect(() => {
		if (initialDedos?.length) {
			const newHands = { derecha: {}, izquierda: {} };

			initialDedos.forEach((dedo) => {
				newHands[dedo.mano_id === 'derecha' ? 'derecha' : 'izquierda'][
					dedo.dedo_id
				] = 1;
			});
			setDerecha({ ...derecha, ...newHands.derecha });
			setIzquierda({ ...izquierda, ...newHands.izquierda });
		}
	}, []);

	// <0...3> , [{dedo_id, mano_id}]
	const setHandsState = (newState, dedos) => {
		const newDerecha = derecha;
		const newIzquierda = izquierda;
		dedos.forEach(({ dedo_id, mano_id }) => {
			if (mano_id === 'derecha') {
				newDerecha[dedo_id] = newState;
			} else {
				newIzquierda[dedo_id] = newState;
			}
		});
		setIzquierda(...[newIzquierda]);
		setDerecha(...[newDerecha]);
	};

	return {
		izquierda,
		derecha,
		onClickDedo,
		setHandsState
	};
}

export function useCaptureBiometrics() {
	const [finishedCapture, setFinishedCapture] = useState(false);
	const [biometricId, setBiometricId] = useState([]);
	const [queue, setQueue] = useState(null);
	const [captured, setCaptured] = useState([]);
	const { iniciarCaptura, newMessage } = useContext(BiometricContext);

	const requestCaptura = async (biometric_id, payload) => {
		return await iniciarCaptura(biometric_id, payload.data);
	};

	const capturar = async (
		biometric_id,
		{ type, user_readable = 'el dedo seleccionado', ...payload }
	) => {
		try {
			if (biometric_id) {
				setBiometricId(biometric_id);
			}
			const response = await requestCaptura(biometric_id, {
				payload,
				data: { type }
			});
			// toast.success(
			// 	`Solicitud para iniciar captura de ${user_readable}`,
			// 	toastOptions
			// );
			return response;
		} catch (e) {
			toast.error(
				`Hubo un error al intentar capturar ${user_readable}`,
				toastOptions
			);
			throw e;
		}
	};

	const iniciarQueueCaptura = (
		dedos,
		biometric_type,
		biometric_id,
		customType = null
	) => {
		setFinishedCapture(false);
		setCaptured([]);
		setQueue([]);
		const dedos_sorted = sortDedosByBiometricType(dedos, biometric_type);
		const newQueue = dedos_sorted.map((dedo) =>
			formatQueuedDedo(dedo, biometric_type, customType)
		);
		if (newQueue.length) {
			if (newQueue[0].biometric_type === 2) {
				newQueue[0].user_readable = `los ${
					newQueue[0].dedo_id === 'pulgar'
						? 'PULGARES'
						: `dedos de la mano ${newQueue[0].mano_id.toUpperCase()}`
				}`;
			}
			capturar(biometric_id, newQueue[0])
				.then((response) => {
					if (!response.isError) {
						const requestId =
							response?.data?.data?.requestId ||
							response?.data?.requestId ||
							response?.requestId;

						if (newQueue[0].biometric_type === 2) {
							const queueWRequestId = newQueue.map((dedo) => {
								return dedo.type === newQueue[0].type
									? { ...dedo, requestId }
									: dedo;
							});
							setQueue(queueWRequestId);
						} else {
							newQueue[0].requestId = requestId;
							setQueue(newQueue);
						}
					}
				})
				.catch((e) => {
					console.log(e);
				});
		}
	};

	const handleNewMessage = async(newMessage) => {
		try {
			console.log(newMessage);
			if (newMessage) {
				if (newMessage.type === 'biometrico_inicia_captura') {
					const dedo = queue?.find((q) => q.requestId === newMessage.requestId);
					if (dedo) {
						toast.success(`Capturando ${dedo.user_readable}`, toastOptions);
					}
				}
				if (newMessage.type === 'biometrico_termina_captura') {
					// console.log(newMessage);
					const dedo = queue?.find((q) => q.requestId === newMessage.requestId);
					// if (dedo) {
					// 	toast.success(
					// 		`Terminó captura para ${dedo.user_readable}`,
					// 		toastOptions
					// 	);
					// }
					let count = 0;
					const queueFiltered = queue?.filter(
						(q) => q.requestId === newMessage.requestId
					);
					const newCaptured = await Promise.all(queueFiltered?.map(async (dedo, index) => {

						const base64wsq = `${newMessage.data.wsqs[index]}`;
						let wsqBlob = await fetch(base64wsq);
						wsqBlob = await wsqBlob.blob();
						const fileWsq = new File([wsqBlob], `${index}.wsq`);

						let pngBlob = await fetch(newMessage.data.pngs[index]);
						pngBlob = await pngBlob.blob();
						const filePng = new File([pngBlob], `${index}.png`);

						dedo['png'] = newMessage.data.pngs[index];
						dedo['wsq'] = newMessage.data.wsqs[index];
						dedo['png_file'] = filePng;
						dedo['wsq_file'] = fileWsq;
						count++;
						return dedo;
					}));
					if (newCaptured?.length) {
						const slice = newCaptured[0];
						setCaptured([...captured, ...newCaptured]);

						const queueCleaned = [...queue];
						const start = queue?.findIndex(
							(q) => q.requestId === newMessage.requestId
						);
						if (start >= 0) {
							const deleteCount =
								slice?.biometric_type === 1 ? 1 : slice?.type === 'P' ? 2 : 4;
							queueCleaned?.splice(start, deleteCount);
						}

						setQueue(queueCleaned);
						if (!!queueCleaned.length) {
							const dedo_sin_capturar = queueCleaned[0];
							if (dedo_sin_capturar?.biometric_type === 2) {
								dedo_sin_capturar.user_readable = `los ${
									dedo_sin_capturar.dedo_id === 'pulgar'
										? 'PULGARES'
										: `dedos de la mano ${dedo_sin_capturar.mano_id.toUpperCase()}`
								}`;
							}
							capturar(biometricId, dedo_sin_capturar)
								.then((response) => {
									if (!response.isError) {
										const requestId =
											response?.data?.data?.requestId ||
											response?.data?.requestId ||
											response?.requestId;

										if (queueCleaned[0].biometric_type === 2) {
											const queueWRequestId = queueCleaned.map((dedo) => {
												return dedo.type === queueCleaned[0].type
													? {...dedo, requestId}
													: dedo;
											});
											setQueue(queueWRequestId);
										} else {
											queueCleaned[0].requestId = requestId;
											setQueue(queueCleaned);
										}
									}
								})
								.catch((e) => {
									console.log(e);
								});
						} else {
							setFinishedCapture(true);
						}
					}
				}
				if (newMessage.type === 'biometrico_conectado') {
					// toast.info('Se ha conectado el biométrico', toastOptions);
				}
				if (newMessage.type === 'biometrico_desconectado') {
					// toast.error('Se ha desconectado el biom����trico', toastOptions);
				}
				// console.log(newMessage);
			}
		} catch (e) {
			console.log(e);
		}
	};

	useEffect(() => {
		if (newMessage) {
			handleNewMessage(newMessage);
		}
	}, [newMessage]);
	return {
		iniciarQueueCaptura,
		queue,
		captured,
		finishedCapture
	};
}
